/**
 * VerifyEmailCallback - Email Verification Handler
 * Flow: Email confirmed → Set session → Activate user → Reload auth → Redirect to dashboard
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/v2/ui/Card';
import { Alert } from '@/components/v2/ui/Alert';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { getSupabaseClient } from '@/lib/supabase-client';
import { apiClient } from '@/lib/api-client';

export function VerifyEmailCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'activating' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get Supabase token from URL hash (Supabase uses hash, not query params)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // Check if this is an email confirmation callback
        if (type !== 'signup' && type !== 'email') {
          setStatus('error');
          setMessage(t('auth.invalidVerificationLink'));
          setTimeout(() => navigate('/v2/login'), 3000);
          return;
        }

        if (!accessToken || !refreshToken) {
          setStatus('error');
          setMessage('Kein Verifizierungstoken gefunden');
          setTimeout(() => navigate('/v2/login'), 3000);
          return;
        }

        // Step 1: Set Supabase session FIRST (so user is authenticated)
        setStatus('activating');
        setMessage(t('auth.verifyingEmail'));

        const supabase = await getSupabaseClient();

        // Set the session
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (sessionError || !sessionData.session) {
          throw new Error('Session konnte nicht gesetzt werden');
        }

        // Step 2: Activate user + workspace via backend
        setMessage('Account wird aktiviert...');

        const response = await fetch('/api/v2/auth/activate-after-email-confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Aktivierung fehlgeschlagen');
        }

        // Step 3: Wait for user to be authenticated via polling
        setMessage('Alles wird vorbereitet...');

        // Wait 2 seconds before starting polling (avoid socket errors on Windows)
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Poll backend to check if user is authenticated (max 15 seconds)
        let authenticated = false;
        let attempts = 0;
        const maxAttempts = 15; // 15 attempts * 1000ms = 15 seconds max

        while (!authenticated && attempts < maxAttempts) {
          try {
            const checkResponse = await apiClient.get('/api/v2/auth/me', {
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            if (checkResponse.data.success && checkResponse.data.user) {
              authenticated = true;
              console.log('[VerifyEmailCallback] User authenticated after', attempts, 'attempts');
              break;
            }
          } catch {
            // Not authenticated yet, retry
            console.log('[VerifyEmailCallback] Auth check failed, retrying...', attempts + 1);
          }

          attempts++;
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry (reduced socket load)
        }

        if (!authenticated) {
          throw new Error('Authentifizierung hat zu lange gedauert (max 15s)');
        }

        // Step 4: Session is ready - minimal final delay
        setMessage('Finalisiere Anmeldung...');

        // Short delay to ensure all services are ready
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Step 5: Success! Navigate to dashboard
        setStatus('success');
        setMessage(t('auth.confirmEmail'));

        // Final short delay
        await new Promise(resolve => setTimeout(resolve, 500));

        // Navigate to dashboard (user is now fully authenticated and activated)
        navigate('/v2/dashboard', { replace: true });

      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
        setTimeout(() => navigate('/v2/login'), 3000);
      }
    };

    verifyEmail();
  }, [navigate, t]);

  return (
    <div className="verify-email-callback min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <div className="p-6 space-y-6">
          <div className="text-center space-y-4">
            {/* Icon based on status */}
            <div className="flex justify-center">
              {(status === 'loading' || status === 'activating') && (
                <Loader2 className="w-16 h-16 text-primary-500 animate-spin" aria-hidden="true" />
              )}
              {status === 'success' && (
                <CheckCircle className="w-16 h-16 text-green-500" aria-hidden="true" />
              )}
              {status === 'error' && (
                <XCircle className="w-16 h-16 text-red-500" aria-hidden="true" />
              )}
            </div>

            {/* Message */}
            <Alert variant={status === 'error' ? 'error' : 'success'}>
              <p className="font-semibold">{message}</p>
            </Alert>

            {status === 'loading' && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('auth.verifyingEmail')}
              </p>
            )}

            {status === 'activating' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('auth.verifyingEmail')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {t('common.loading')}
                </p>
              </div>
            )}

            {status === 'success' && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('nav.dashboard')}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
